import { Eye } from 'lucide-react'
import { IconButton, Popover } from '@/components/play'
import { AthleteCard } from '@/components/map/course-map/AthleteCard'
import type { AthleteRecord } from '@/lib/athletes/types'
import { DEFAULT_STALE_AFTER_MS } from '@/lib/watch/eventAthletes'
import type { RaceLook } from './RaceLabel'

interface Props {
  athlete: AthleteRecord
  look: RaceLook | undefined
  /** Now, in epoch milliseconds: the card's age is read from it. */
  now: number
}

/**
 * An athlete's card as viewers see it on the event map (`AthleteCard`), from the roster: their photo or initials,
 * name, bib, race and how old their position is, and below it where that position is, or why viewers don't see it.
 */
export function AthletePreview({ athlete, look, now }: Props) {
  const { location } = athlete
  const position = location ? { latitude: location.latitude, longitude: location.longitude, recordedAt: Date.parse(location.recorded_at) } : null
  const stale = position ? now - position.recordedAt > DEFAULT_STALE_AFTER_MS : false

  return (
    <Popover
      label={`${athlete.name}’s card`}
      side="left"
      trigger={
        <IconButton label={`Preview ${athlete.name}’s card`} variant="ghost">
          <Eye />
        </IconButton>
      }
    >
      <div className="space-y-3">
        <AthleteCard
          variant="plain"
          athlete={{ id: athlete.id, name: athlete.name, bib: athlete.bib, gender: athlete.gender, routeId: athlete.route_id, photoUrl: athlete.photo_url }}
          position={position}
          race={look ? { name: look.name, color: look.color } : null}
          stale={stale}
          now={now}
        />
        <p className="text-xs text-neutral-7">
          {athlete.hidden
            ? 'Hidden from the public page: viewers don’t see this card.'
            : position
              ? `Last position ${position.latitude.toFixed(5)}, ${position.longitude.toFixed(5)}.`
              : 'Viewers see them once their tracker reports.'}
        </p>
      </div>
    </Popover>
  )
}
